import type {
  JSONContent,
  MarkdownParseHelpers,
  MarkdownRendererHelpers,
  MarkdownToken,
} from '@tiptap/core';

// `@tiptap/markdown` has no handler for marked's `escape` inline token and
// drops it; keep the character as plain text (`\-` → `-`), recursing so
// escapes nested in marks (`**a \* b**`) survive too. The renderer below
// emits such escapes.
export const retainEscapesAsText = (tokens: MarkdownToken[]): MarkdownToken[] =>
  tokens.map((inlineToken) => {
    const mapped =
      inlineToken.type === 'escape'
        ? { ...inlineToken, type: 'text' }
        : { ...inlineToken };
    if (Array.isArray(mapped.tokens)) mapped.tokens = retainEscapesAsText(mapped.tokens);
    return mapped;
  });

/**
 * An empty paragraph has no markdown form, so it persists as this entity.
 * Storage-only vocabulary: presentation consumers writing a plain-text
 * clipboard flavor must strip it, or the paste shows the entity verbatim.
 */
export const PARAGRAPH_BLANK_LINE_MARKER = '&nbsp;';

// Inverse of the renderer's blank-line marker below. marked keeps HTML
// entities as literal text, so without this a saved empty paragraph re-parses
// as a visible "&nbsp;". Marked text (`**&nbsp;**`) stays literal.
export const isBlankLineMarker = (content: JSONContent[]): boolean =>
  content.length === 1 &&
  content[0]!.type === 'text' &&
  content[0]!.text === PARAGRAPH_BLANK_LINE_MARKER &&
  !content[0]!.marks?.length;

/**
 * Splits a paragraph at each soft line break so a single `\n` becomes a
 * sibling paragraph, matching the vMarkdown editor.
 *
 * `@tiptap/markdown` (marked, CommonMark) keeps a single `\n` as a soft break
 * *inside* one paragraph's text node — e.g. a generated note's
 * `Follow-up instructions and timeline:\nScheduled …` parses to one paragraph
 * with a literal `\n`. That representation only renders as two lines because
 * the node view applies `white-space: pre-wrap`; ProseMirror's paragraph does
 * not preserve whitespace, so the first keystroke re-parses the line through
 * `readDOMChange` and collapses the `\n` to a space, merging the two lines. The
 * vMarkdown editor avoids this because its HTML pipeline promotes every single
 * `\n` to a separate `<p>`. This handler gives the markdown-native editor the
 * same shape: split the parsed inline content at each soft break into one
 * paragraph per line.
 *
 * Grammar stays delegated to marked — only the already-lexed inline content is
 * regrouped, so fences, tables, and lists (parsed via their own paths) keep
 * their soft breaks. A hard break (`hardBreak` node) is left inline; only the
 * literal `\n` soft break splits. Marks survive the split because each text
 * fragment is cloned with its original `marks`.
 */
export const parseParagraphMarkdown = (
  token: MarkdownToken,
  helpers: MarkdownParseHelpers
): JSONContent[] => {
  const inline = helpers.parseInline(retainEscapesAsText(token.tokens ?? []));

  const lines: JSONContent[][] = [[]];
  for (const node of inline) {
    if (
      node.type === 'text' &&
      typeof node.text === 'string' &&
      node.text.includes('\n')
    ) {
      node.text.split('\n').forEach((segment, index) => {
        if (index > 0) lines.push([]);
        if (segment.length > 0) lines[lines.length - 1]!.push({ ...node, text: segment });
      });
    } else {
      lines[lines.length - 1]!.push(node);
    }
  }

  const paragraphs = lines
    .filter((content) => content.length > 0)
    .map((content) =>
      isBlankLineMarker(content)
        ? { type: 'paragraph', content: [] }
        : { type: 'paragraph', content }
    );

  // A paragraph token with no rendered content (e.g. only whitespace) still
  // needs one empty paragraph so `block+` parents stay valid.
  return paragraphs.length > 0 ? paragraphs : [{ type: 'paragraph', content: [] }];
};

/**
 * Default paragraph rendering (inline children on one line; the blank-line
 * marker keeps an empty paragraph a paragraph), plus one escape: a paragraph
 * that *opens* with a list marker would re-lex as a list item, so the marker
 * serializes escaped (`\-`, `1\.`).
 *
 * Three shapes need this. A lone marker — `Label:` above a bare bullet, which
 * the soft-break split above can leave behind, a common generated-note shape.
 * A marker with text after it: neither list input rule fires inside an existing
 * list item, so typing `- 1. part water` leaves a bullet holding the literal
 * text `1. part water`; unescaped it re-lexes as a nested ordered list on the
 * next load and the typed number silently becomes a marker. And the same with
 * leading indent — typing `  - 2. parts tequila` inside an item keeps the
 * spaces in the text, and an anchored match would skip right past them.
 *
 * That indent is dropped rather than emitted, because leading whitespace has no
 * paragraph form in markdown: marked eats a shallow indent and lexes four or
 * more columns as an indented code block. Emitted verbatim, the third typed
 * level (`    - 1. part water`, or two columns inside an item that already
 * indents its content by two) reloaded as a code fence instead of text.
 */
export const renderParagraphMarkdown = (
  node: JSONContent,
  helpers: MarkdownRendererHelpers
): string => {
  const content = node.content ?? [];
  if (content.length === 0) return PARAGRAPH_BLANK_LINE_MARKER;
  const rendered = helpers.renderChildren(content).replace(/^[^\S\n]+/, '');
  if (rendered.length === 0) return PARAGRAPH_BLANK_LINE_MARKER;
  // The `\s|$` tail escapes the marker whether it stands alone or leads text.
  const marker = /^(?:([-*+])|(\d{1,9})([.)]))(\s|$)/.exec(rendered);
  if (!marker) return rendered;
  const [, bullet, digits, delimiter] = marker;
  const escaped = bullet ? `\\${bullet}` : `${digits}\\${delimiter}`;
  return `${escaped}${rendered.slice(bullet ? 1 : digits!.length + 1)}`;
};

// The doc/section joins sibling paragraphs with the blank line that
// round-trips as a paragraph break.
export const paragraphMarkdownSpec = {
  parseMarkdown: parseParagraphMarkdown,
  renderMarkdown: renderParagraphMarkdown,
};
